import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Map, Upload, ArrowLeft, X, Layers3 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  getVegetationPolygons,
  getPolygonSummary,
  uploadGeoJSONPolygons,
  deleteVegetationPolygon,
  getPaddocks,
  getDroneRuns,
} from '../../services/api'
import StatCard from '../../components/StatCard'

const VEG_CLASSES = [
  { value: 'pasture_grass', label: 'Pasture Grass', color: 'bg-green-100 text-green-700' },
  { value: 'lantana', label: 'Lantana', color: 'bg-red-100 text-red-700' },
  { value: 'other_shrub', label: 'Other Shrub', color: 'bg-amber-100 text-amber-700' },
  { value: 'tree_canopy', label: 'Tree Canopy', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'bare_soil', label: 'Bare Soil', color: 'bg-stone-100 text-stone-700' },
  { value: 'water', label: 'Water', color: 'bg-blue-100 text-blue-700' },
]

const classColor = (cls) => VEG_CLASSES.find(v => v.value === cls)?.color || 'bg-gray-100 text-gray-700'
const classLabel = (cls) => VEG_CLASSES.find(v => v.value === cls)?.label || cls

export default function VegetationPolygons({ ranchId }) {
  const queryClient = useQueryClient()
  const [showUpload, setShowUpload] = useState(false)
  const [filterClass, setFilterClass] = useState('')

  const { data: polygons, isLoading } = useQuery({
    queryKey: ['polygons', ranchId, filterClass],
    queryFn: () => getVegetationPolygons(ranchId, filterClass ? { vegetation_class: filterClass } : {}),
  })

  const { data: summary } = useQuery({
    queryKey: ['polygonSummary', ranchId],
    queryFn: () => getPolygonSummary(ranchId),
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const { data: droneRuns } = useQuery({
    queryKey: ['droneRuns', ranchId],
    queryFn: () => getDroneRuns(ranchId),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteVegetationPolygon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['polygons'] })
      queryClient.invalidateQueries({ queryKey: ['polygonSummary'] })
    },
  })

  const totalPolygons = polygons?.length || 0
  const totalAreaHa = summary?.reduce((sum, s) => sum + Number(s.total_area_ha || 0), 0) || 0

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['polygons'] })
    queryClient.invalidateQueries({ queryKey: ['polygonSummary'] })
    setShowUpload(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vegetation Polygons</h1>
            <p className="text-gray-500 mt-1">
              Versioned class labels — the training signal that teaches satellite classifiers
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Upload className="h-4 w-4 mr-2" /> Upload GeoJSON
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Total Polygons" value={totalPolygons} icon={<Map className="h-5 w-5" />} />
        <StatCard title="Labelled Area" value={`${totalAreaHa.toFixed(1)} ha`} icon={<Layers3 className="h-5 w-5" />} />
        <StatCard title="Classes Mapped" value={summary?.length || 0} icon={<Map className="h-5 w-5" />} />
      </div>

      {/* Area by class */}
      {summary && summary.length > 0 && (
        <div className="card p-4">
          <h3 className="font-medium text-gray-800 mb-3">Area by Vegetation Class (latest version)</h3>
          <div className="flex flex-wrap gap-2">
            {summary.map(s => (
              <div key={s.vegetation_class} className={`px-3 py-1.5 rounded-full text-sm ${classColor(s.vegetation_class)}`}>
                <span className="font-medium">{classLabel(s.vegetation_class)}</span>
                <span className="ml-2 font-mono">{Number(s.total_area_ha).toFixed(1)} ha</span>
                <span className="ml-1 opacity-70 text-xs">({s.polygon_count})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterClass('')}
          className={`px-3 py-1 rounded-full text-sm font-medium ${!filterClass ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          All
        </button>
        {VEG_CLASSES.map(vc => (
          <button key={vc.value} onClick={() => setFilterClass(vc.value)}
            className={`px-3 py-1 rounded-full text-sm font-medium ${filterClass === vc.value ? vc.color : 'bg-gray-100 text-gray-600'}`}>
            {vc.label}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : totalPolygons === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No polygons yet. Upload a GeoJSON FeatureCollection to begin building training labels.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Mapped</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paddock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Area</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {polygons.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{format(parseISO(p.date_mapped), 'dd MMM yyyy')}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${classColor(p.vegetation_class)}`}>
                      {classLabel(p.vegetation_class)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{p.paddock_name || '-'}</td>
                  <td className="px-4 py-3 text-sm font-mono">{p.area_ha ? `${Number(p.area_ha).toFixed(3)} ha` : '-'}</td>
                  <td className="px-4 py-3 text-sm">{p.label_source}</td>
                  <td className="px-4 py-3 text-sm">v{p.mapping_version}</td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {p.confidence_score != null ? Number(p.confidence_score).toFixed(3) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => {
                        if (confirm(`Delete this ${classLabel(p.vegetation_class)} polygon?`)) {
                          deleteMutation.mutate(p.id)
                        }
                      }}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showUpload && (
        <UploadGeoJSONModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          droneRuns={droneRuns || []}
          onClose={() => setShowUpload(false)}
          onSuccess={invalidate}
        />
      )}
    </div>
  )
}

function UploadGeoJSONModal({ ranchId, paddocks, droneRuns, onClose, onSuccess }) {
  const [form, setForm] = useState({
    paddock_id: '',
    drone_run_id: '',
    date_mapped: new Date().toISOString().split('T')[0],
    mapping_version: 1,
    label_source: 'manual',
    default_class: 'pasture_grass',
    geojson_text: '',
  })
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  const mutation = useMutation({
    mutationFn: uploadGeoJSONPolygons,
    onSuccess: (data) => {
      setResult(data)
      if (data.inserted_count > 0) setTimeout(onSuccess, 1500)
    },
    onError: (err) => setError(err.response?.data?.detail || 'Upload failed'),
  })

  const update = (field, val) => setForm(prev => ({ ...prev, [field]: val }))

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const text = await file.text()
    update('geojson_text', text)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError(null)
    setResult(null)
    let fc
    try {
      fc = JSON.parse(form.geojson_text)
    } catch (err) {
      setError('Invalid JSON — check the GeoJSON text')
      return
    }
    if (fc.type !== 'FeatureCollection') {
      setError('GeoJSON must be a FeatureCollection')
      return
    }
    mutation.mutate({
      ranch_id: ranchId,
      paddock_id: form.paddock_id || null,
      drone_run_id: form.drone_run_id || null,
      date_mapped: form.date_mapped,
      mapping_version: parseInt(form.mapping_version, 10),
      label_source: form.label_source,
      default_class: form.default_class,
      feature_collection: fc,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Upload className="h-5 w-5 text-green-600" />
            Upload GeoJSON FeatureCollection
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          {result && (
            <div className="text-sm bg-green-50 text-green-700 p-2 rounded">
              {result.inserted_count} polygon(s) inserted, {result.skipped_count} skipped
              {result.skipped_count > 0 && result.skipped && (
                <ul className="mt-1 text-xs">
                  {result.skipped.slice(0, 5).map((s, i) => (
                    <li key={i}>#{s.index}: {s.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            Each feature's <code>properties.vegetation_class</code> sets the label (else default applies).
            <code>properties.paddock_id</code> and <code>properties.confidence_score</code> are also honoured.
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Date Mapped *</label>
              <input type="date" className="input" value={form.date_mapped}
                onChange={e => update('date_mapped', e.target.value)} />
            </div>
            <div>
              <label className="label">Mapping Version</label>
              <input type="number" className="input" min="1" value={form.mapping_version}
                onChange={e => update('mapping_version', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Label Source</label>
              <select className="input" value={form.label_source}
                onChange={e => update('label_source', e.target.value)}>
                <option value="manual">Manual</option>
                <option value="classifier">Classifier</option>
                <option value="hybrid">Hybrid (classifier + manual review)</option>
              </select>
            </div>
            <div>
              <label className="label">Default Class (if feature lacks one)</label>
              <select className="input" value={form.default_class}
                onChange={e => update('default_class', e.target.value)}>
                {VEG_CLASSES.map(vc => <option key={vc.value} value={vc.value}>{vc.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Default Paddock (optional)</label>
              <select className="input" value={form.paddock_id}
                onChange={e => update('paddock_id', e.target.value)}>
                <option value="">— Per-feature —</option>
                {paddocks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Drone Run (optional)</label>
              <select className="input" value={form.drone_run_id}
                onChange={e => update('drone_run_id', e.target.value)}>
                <option value="">— None —</option>
                {droneRuns.map(r => (
                  <option key={r.id} value={r.id}>
                    {format(parseISO(r.flight_date), 'dd MMM yyyy')} ({r.sensors || 'unknown'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">GeoJSON File</label>
            <input type="file" accept=".json,.geojson" className="input" onChange={handleFileChange} />
          </div>

          <div>
            <label className="label">GeoJSON Text *</label>
            <textarea className="input font-mono text-xs" rows={6}
              placeholder='{"type":"FeatureCollection","features":[...]}'
              value={form.geojson_text}
              onChange={e => update('geojson_text', e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
            <button type="submit" disabled={mutation.isPending || !form.geojson_text}
              className="btn bg-green-600 text-white hover:bg-green-700">
              {mutation.isPending ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
